import { Routes } from '@angular/router';
import {
  ENGLISH_CONTENT_ROUTE_ENTRIES,
  LOCALIZED_CONTENT_LANGUAGES,
  LOCALIZED_CONTENT_ROUTE_ENTRIES,
} from './content/content-route-manifest.generated';
import { SUPPORTED_LANGUAGE_CODES } from './core/supported-language-codes.generated';
import { LEGAL_PAGES } from './legal/legal-pages';

const contentRoutes: Routes = ENGLISH_CONTENT_ROUTE_ENTRIES.map((page) => ({
  path: `en/content/${page.slug}`,
  loadComponent: () =>
    import('./content/content-page').then((module) => module.ContentPageComponent),
  title: `${page.title} | IQLabTest`,
  data: { contentSlug: page.slug, contentLanguage: 'en' },
}));

const localizedContentIndexRoutes: Routes = LOCALIZED_CONTENT_LANGUAGES.map((language) => ({
  path: `${language}/content`,
  loadComponent: () =>
    import('./content/content-page').then((module) => module.ContentPageComponent),
  title: `IQ knowledge center | IQLabTest`,
  data: { contentLanguage: language },
}));

const localizedContentRoutes: Routes = LOCALIZED_CONTENT_ROUTE_ENTRIES.map(
  ({ language, slug, title }) => ({
    path: `${language}/content/${slug}`,
    loadComponent: () =>
      import('./content/content-page').then((module) => module.ContentPageComponent),
    title: `${title} | IQLabTest`,
    data: { contentSlug: slug, contentLanguage: language },
  }),
);

const aboutRoutes: Routes = SUPPORTED_LANGUAGE_CODES.map((language) => ({
  path: `${language}/about`,
  loadComponent: () => import('./public/landing-page').then((module) => module.LandingPage),
  title: 'About IQLabTest',
  data: { language },
}));

const legalRoutes: Routes = LEGAL_PAGES.map((page) => ({
  path: `en/legal/${page.slug}`,
  loadComponent: () => import('./legal/legal-page').then((module) => module.LegalPageComponent),
  title: `${page.title} | IQLabTest`,
  data: { legalSlug: page.slug },
}));

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./iq-test/iq-test-page').then((module) => module.IqTestPage),
    title: 'Cognitive assessment — clear, careful, indicative',
  },
  {
    path: 'en/content',
    loadComponent: () =>
      import('./content/content-page').then((module) => module.ContentPageComponent),
    title: 'IQ knowledge center | IQLabTest',
    data: { contentLanguage: 'en' },
  },
  ...contentRoutes,
  ...localizedContentIndexRoutes,
  ...localizedContentRoutes,
  {
    path: 'en/legal',
    loadComponent: () => import('./legal/legal-page').then((module) => module.LegalPageComponent),
    title: 'Legal center | IQLabTest',
  },
  ...legalRoutes,
  {
    path: 'design-system/examples',
    loadComponent: () =>
      import('./design-system/examples/example-screens-page').then(
        (module) => module.ExampleScreensPage,
      ),
    title: 'Reference Screens | IQLabTest Design System',
  },
  {
    path: 'design-system',
    loadComponent: () =>
      import('./design-system/design-system-page').then((module) => module.DesignSystemPage),
    title: 'IQLabTest Design System',
  },
  {
    path: 'en/methodology',
    loadComponent: () => import('./public/trust-page').then((module) => module.TrustPageComponent),
    title: 'Assessment methodology | IQLabTest',
    data: { trustPage: 'methodology' },
  },
  {
    path: 'en/editorial-standards',
    loadComponent: () => import('./public/trust-page').then((module) => module.TrustPageComponent),
    title: 'Editorial standards | IQLabTest',
    data: { trustPage: 'editorial' },
  },
  { path: 'methodology', redirectTo: 'en/methodology', pathMatch: 'full' },
  { path: 'editorial-standards', redirectTo: 'en/editorial-standards', pathMatch: 'full' },
  {
    path: 'account',
    loadComponent: () => import('./account/account-page').then((module) => module.AccountPage),
    title: 'Your assessment history | IQLabTest',
  },
  {
    path: 'practice',
    loadComponent: () => import('./practice/practice-page').then((module) => module.PracticePage),
    title: 'Daily IQ challenge and reasoning practice | IQLabTest',
  },
  {
    path: 'accessibility',
    loadComponent: () =>
      import('./accessibility/accessibility-page').then((module) => module.AccessibilityPage),
    title: 'Accessibility preferences | IQLabTest',
  },
  { path: 'about', redirectTo: 'en/about', pathMatch: 'full' },
  ...aboutRoutes,
  {
    path: 'verify/:testId',
    loadComponent: () =>
      import('./iq-test/iq-verification-page').then((module) => module.IqVerificationPage),
    title: 'Verify assessment result — IQLabTest',
  },
  {
    path: 'company',
    loadComponent: () => import('./company/company-page').then((module) => module.CompanyPage),
    title: 'Company workspace — IQLabTest',
  },
  {
    path: 'candidate',
    loadComponent: () =>
      import('./candidate/candidate-page').then((module) => module.CandidatePage),
    title: 'Candidate assessment — IQLabTest',
  },
  {
    path: 'consumer',
    loadComponent: () => import('./consumer/consumer-page').then((module) => module.ConsumerPage),
    title: 'Personal assessment — IQLabTest',
  },
  {
    path: 'admin',
    loadComponent: () => import('./admin/admin-page').then((module) => module.AdminPage),
    title: 'Administration — IQLabTest',
  },
  {
    path: '404',
    loadComponent: () => import('./public/not-found-page').then((module) => module.NotFoundPage),
    title: 'Page not found | IQLabTest',
  },
  {
    path: '**',
    loadComponent: () => import('./public/not-found-page').then((module) => module.NotFoundPage),
    title: 'Page not found | IQLabTest',
  },
];
